import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData } from "react-router";
import { SITE_NAME, API_BASE } from "@/lib/constants";
import { safeFetchJson } from "@/lib/fetch-json";
import type { CryptoResult } from "@/lib/api";
import CryptoPage from "@/pages/CryptoPage";

export async function loader({ params }: LoaderFunctionArgs) {
  const symbol = params.symbol!.toLowerCase();
  const crypto = await safeFetchJson<CryptoResult>(`${API_BASE}/crypto/${symbol.toUpperCase()}`, {
    timeoutMs: 3000,
  });
  return { symbol, crypto };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const symbol = data?.symbol ?? "";
  const crypto = data?.crypto;
  const origin = "https://loadlogo.com";

  if (crypto && !crypto.error) {
    const name = crypto.name || symbol.toUpperCase();
    const description = `Download the ${name} (${symbol.toUpperCase()}) cryptocurrency logo. Get crypto logos via the ${SITE_NAME} API.`;
    return [
      { title: `${name} (${symbol.toUpperCase()}) Logo | ${SITE_NAME}` },
      { name: "description", content: description },
      { property: "og:title", content: `${name} (${symbol.toUpperCase()}) Logo | ${SITE_NAME}` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${origin}/og/crypto/${symbol}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:url", content: `${origin}/crypto/${symbol}` },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${name} (${symbol.toUpperCase()}) Logo | ${SITE_NAME}` },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: `${origin}/og/crypto/${symbol}` },
      { tagName: "link", rel: "canonical", href: `${origin}/crypto/${symbol}` },
    ];
  }

  return [
    { title: `${symbol.toUpperCase()} Crypto Logo | ${SITE_NAME}` },
    {
      name: "description",
      content: `Download the ${symbol.toUpperCase()} cryptocurrency logo via the ${SITE_NAME} API.`,
    },
    { property: "og:title", content: `${symbol.toUpperCase()} Crypto Logo | ${SITE_NAME}` },
    {
      property: "og:description",
      content: `Download the ${symbol.toUpperCase()} cryptocurrency logo via the ${SITE_NAME} API.`,
    },
    { property: "og:type", content: "website" },
    { property: "og:image", content: `${origin}/og/crypto/${symbol}` },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:url", content: `${origin}/crypto/${symbol}` },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { tagName: "link", rel: "canonical", href: `${origin}/crypto/${symbol}` },
  ];
};

export default function CryptoRoute() {
  const { crypto } = useLoaderData<typeof loader>();
  return <CryptoPage serverData={crypto} />;
}
